import * as React from "react";

export type GridSortModel = { field: string; sort: "asc" | "desc" }[];

export interface GridPaginationOptions {
    pageSize?: number;
    defaultSort?: GridSortModel;
    dateFields?: Record<string, string>;
}

const DEFAULT_PAGE_SIZE = 10;

const defaultCompare = (a: any, b: any): number => {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true });
};

export const useGridPagination = <T>(rows: T[], options?: GridPaginationOptions) => {
    const dateFields = options?.dateFields;

    const [page, setPage] = React.useState<number>(1);
    const [pageSize, setPageSize] = React.useState<number>(
        options?.pageSize ?? DEFAULT_PAGE_SIZE
    );
    const [sortModel, setSortModel] = React.useState<GridSortModel>(
        options?.defaultSort ?? []
    );

    React.useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(rows.length / pageSize));
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [rows.length, pageSize, page]);

    const sortedRows = React.useMemo(() => {
        if (!sortModel.length) return rows;
        const { field, sort } = sortModel[0];
        const dir = sort === "desc" ? -1 : 1;
        const valueOf = (row: any) => {
            const dateField = dateFields?.[field];
            if (!dateField) return row?.[field];
            const time = new Date(row?.[dateField] ?? row?.[field]).getTime();
            return Number.isNaN(time) ? null : time;
        };
        return [...rows].sort((a, b) => dir * defaultCompare(valueOf(a), valueOf(b)));
    }, [rows, sortModel, dateFields]);

    const pagedRows = React.useMemo(() => {
        const start = (page - 1) * pageSize;
        return sortedRows.slice(start, start + pageSize);
    }, [sortedRows, page, pageSize]);

    const server = React.useMemo(
        () => ({
            page,
            pageSize,
            total: rows.length,
            sortModel,
            onChange: (nextPage: number, nextPageSize?: number) => {
                setPage(nextPage);
                if (nextPageSize) setPageSize(nextPageSize);
            },
            onSortChange: (model: GridSortModel) => {
                setSortModel(model ?? []);
                setPage(1);
            },
        }),
        [page, pageSize, rows.length, sortModel]
    );

    return { pagedRows, server };
};
